// Copyright (c) 2026 Joshua S. Plaza. All rights reserved.

import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Class as ClassIcon,
  Subject as SubjectIcon,
  Groups as GroupsIcon,
  Assignment as AssignmentIcon,
  CalendarMonth as CalendarIcon,
  Announcement as AnnouncementIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Grade as GradeIcon,
  EventNote as EventNoteIcon,
  Description as ReportIcon,
  CloudUpload as CloudUploadIcon,
  History as HistoryIcon,
  FamilyRestroom as FamilyRestroomIcon,
  EmojiEvents as EmojiEventsIcon,

  Home as HomeIcon,
} from '@mui/icons-material';
import { logout } from '../store/slices/authSlice';
import { toggleSidebar, setSidebarOpen } from '../store/slices/uiSlice';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import PageWrapper from '../components/PageWrapper';

import { API_BASE_URL } from '../services/api';

const MEDIA_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');
const drawerWidth = 260;

// Helper function to get profile picture URL
const getProfilePictureUrl = (user) => {
  if (user?.profile_picture) {
    if (user.profile_picture.startsWith('http')) return user.profile_picture;
    return `${MEDIA_BASE_URL}${user.profile_picture}`;
  }
  return null;
};

const MainLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { sidebarOpen } = useSelector((state) => state.ui);

  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleDrawerToggle = () => {
    dispatch(toggleSidebar());
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      dispatch(setSidebarOpen(false));
    }
  };

  // Define menu items based on user role
  const getMenuItems = () => {
    switch (user?.role) {
      case 'ADMIN':
        return [
          { text: 'Home', icon: <HomeIcon />, path: '/' },
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
          { text: 'Users', icon: <PeopleIcon />, path: '/admin/users' },
          { text: 'School Years', icon: <CalendarIcon />, path: '/admin/school-years' },
          { text: 'Sections', icon: <GroupsIcon />, path: '/admin/sections' },
          { text: 'Subjects', icon: <SubjectIcon />, path: '/admin/subjects' },
          { text: 'Classes', icon: <ClassIcon />, path: '/admin/classes' },
          { text: 'Enrollments', icon: <AssignmentIcon />, path: '/admin/enrollments' },
          { text: 'Reports', icon: <ReportIcon />, path: '/admin/reports' },
          { text: 'Honor Roll', icon: <EmojiEventsIcon />, path: '/admin/honor-roll' },
          { text: 'Announcements', icon: <AnnouncementIcon />, path: '/admin/announcements' },
          { text: 'Backup & Restore', icon: <CloudUploadIcon />, path: '/admin/backup' },
          { text: 'Audit Logs', icon: <HistoryIcon />, path: '/admin/audit-logs' },
        ];
      case 'TEACHER':
        return [
          { text: 'Home', icon: <HomeIcon />, path: '/' },
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/teacher' },
                { text: 'My Classes', icon: <ClassIcon />, path: '/teacher/classes' },
                { text: 'My Advisory', icon: <GroupsIcon />, path: '/teacher/advisory' },
          { text: 'Gradebook', icon: <GradeIcon />, path: '/teacher/gradebook' },
          { text: 'Attendance', icon: <EventNoteIcon />, path: '/teacher/attendance' },
          { text: 'Guardians', icon: <FamilyRestroomIcon />, path: '/teacher/guardians' },
          { text: 'Reports', icon: <ReportIcon />, path: '/teacher/reports' },
          { text: 'Announcements', icon: <AnnouncementIcon />, path: '/teacher/announcements' },
        ];
      case 'STUDENT':
        return [
          { text: 'Home', icon: <HomeIcon />, path: '/' },
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/student' },
          { text: 'My Grades', icon: <GradeIcon />, path: '/student/grades' },
          { text: 'My Attendance', icon: <EventNoteIcon />, path: '/student/attendance' },
          { text: 'Announcements', icon: <AnnouncementIcon />, path: '/student/announcements' },
        ];
      case 'PARENT':
        return [
          { text: 'Home', icon: <HomeIcon />, path: '/' },
          { text: 'Dashboard', icon: <DashboardIcon />, path: '/parent' },
          { text: 'Announcements', icon: <AnnouncementIcon />, path: '/parent/announcements' },
        ];
      default:
        return [];
    }
  };

  const menuItems = getMenuItems();

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={() => handleNavigate(`/${user?.role?.toLowerCase()}`)}
      >
        <Box
          component="img"
          src="/rnhs-logo.png"
          alt="RNHS Logo"
          sx={{
            width: 48,
            height: 48,
            objectFit: 'contain',
          }}
        />
        <Typography variant="h6" fontWeight="bold" color="primary">
          RNHS Portal
        </Typography>
      </Box>

      <List sx={{ flex: 1, pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ px: 1 }}>
            <ListItemButton
              onClick={() => handleNavigate(item.path)}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                  '& .MuiListItemText-primary': {
                    color: 'primary.main',
                    fontWeight: 600,
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        color="inherit"
        sx={(theme) => ({
          zIndex: theme.zIndex.drawer + 1,
          width: { md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: sidebarOpen ? `${drawerWidth}px` : 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          color: theme.palette.text.primary,
          backdropFilter: 'blur(12px)',
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        })}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find((item) => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              onClick={() => handleNavigate(`/${user?.role?.toLowerCase()}/profile`)}
              sx={{
                display: { xs: 'none', sm: 'block' },
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            >
              {user?.full_name}
            </Typography>
            <IconButton onClick={handleProfileMenuOpen} color="inherit">
              <Avatar
                src={getProfilePictureUrl(user)}
                sx={{ width: 36, height: 36, bgcolor: 'secondary.main' }}
              >
                {user?.first_name?.[0]}
              </Avatar>
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
          >
            <MenuItem onClick={() => { handleProfileMenuClose(); handleNavigate(`/${user?.role?.toLowerCase()}/profile`); }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: sidebarOpen ? drawerWidth : 0 }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={isMobile ? sidebarOpen : false}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: { xs: '100%', sm: drawerWidth } },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="persistent"
          open={sidebarOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          minHeight: '100vh',
          backgroundColor: 'background.default',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <Box sx={{ flex: 1 }}>
          <PageWrapper>
            <Outlet />
          </PageWrapper>
        </Box>
        <Footer variant="main" />
      </Box>
      <BackToTop />
    </Box>
  );
};

export default MainLayout;
